import React, {
    ReactElement,
} from "react";
import { ChannelMenuAction, ChatSettingsChannelMenu } from "../../../../../__generated__/api";
import Editor from "../../../../../components/agent-v3/editor/Editor";
import { ProxyProperty } from "../../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../../components/expanding-section/AdvancedSection";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import Select from "../../../../../components/select/Select";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";

const ACTION_TYPES = {
    [ChannelMenuAction.Disabled]: "Disabled",
    [ChannelMenuAction.CreatePublicTicket]: "Create a public ticket and thread",
    [ChannelMenuAction.CreatePrivateTicket]: "Create a private ticket and thread",
    [ChannelMenuAction.TextOnly]: "Only display floating text",
};

export interface Props {
    data: ProxyProperty<ChatSettingsChannelMenu | undefined | null>,
}

const DEFAULT_MENU_TEXT = "Press \"Create Request\" to get started...";

export default function ChannelMenuSettings({
    data,
}: Props): ReactElement {
    const properties = data.useBuilder(builder => builder
        .property("action", current => current?.action ?? ChannelMenuAction.Disabled)
        .property("menuText", current => current?.menuText)
        .property("menuButtonText", current => current?.menuButtonText ?? "")
        .property("afterText", current => current?.afterText)
        .property("afterImageUrl", current => current?.afterImageUrl));

    const isMenuEnabled = properties.action.value !== ChannelMenuAction.Disabled
        && properties.action.value !== ChannelMenuAction.TextOnly;
    return (

        <SettingsContainer.Section
            title="Channel Menu"
            helpText=" This option allows you to configure a message and menu
                that will always live at the bottom of the chat. If a user
                types a message in a channel, the channel menu will automatically
                move to the bottom."
        >
            <SettingsContainer.Setting
                title="Action"
                description="What type of action should occur when the user clicks the channel menu button."
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <Select
                    className={SettingsContainer.Setting.PickerClassName}
                    placeholder=""
                    onChange={value => properties.action.setValue(value.value as ChannelMenuAction)}
                    options={Object.entries(ACTION_TYPES).map(([value, label]) => ({ value, label }))}
                    value={!properties.action.value ? undefined : {
                        value: properties.action.value,
                        label: ACTION_TYPES[properties.action.value as keyof typeof ACTION_TYPES],
                    }}
                />
            </SettingsContainer.Setting>

            <SettingsContainer.Setting
                title="Channel Text"
                description=" The main text to display in the channel."
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                isVisible={properties.action.value !== ChannelMenuAction.Disabled}
            >
                <Editor
                    value={properties.menuText.value?.rawText ?? DEFAULT_MENU_TEXT}
                    onChange={rawText => properties.menuText.setValue({ rawText })}
                    size="medium"
                    hasBorder
                    placeholder="Hey, please remember to use a thread..."
                />
            </SettingsContainer.Setting>

            {isMenuEnabled && (
                <AdvancedSection title="More Settings">
                    <SettingsContainer.Setting
                        title="Button Text"
                        description="The text of the button that the user will click."
                        permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                        isVisible={properties.action.value !== ChannelMenuAction.Disabled}
                    >
                        <FormGroup
                            type="text"
                            placeholder="Create Request"
                            onChange={evt => properties.menuButtonText.setValue(evt.target.value)}
                            value={properties.menuButtonText.value}
                            size="small"
                            className={SettingsContainer.Setting.PickerClassName}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="After Click Image"
                        isOptional
                        description=" To help the user understand the next steps, you can add an
                            image and text that will be send to the user after they fill out the request.
                            The text should say something about how they can continue in the direct
                            message sent from the Agent bot (or whatever you renamed it to)."
                        permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                        isVisible={properties.action.value !== ChannelMenuAction.Disabled}
                    >
                        <IconUploader
                            value={properties.afterImageUrl.value as string}
                            onChange={properties.afterImageUrl.setValue}
                            shouldStore
                            size="xl"
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="After Click Text"
                        isOptional
                        description="This message should inform the user of next steps."
                        permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                        isVisible={properties.action.value !== ChannelMenuAction.Disabled}
                    >
                        <Editor
                            value={properties.afterText.value?.rawText ?? ""}
                            onChange={rawText => properties.afterText.setValue({ rawText })}
                            size="medium"
                            hasBorder
                            placeholder="Hey, please remember to use a thread..."
                        />
                    </SettingsContainer.Setting>

                </AdvancedSection>
            )}

        </SettingsContainer.Section>

    );
}

ChannelMenuSettings.defaultProps = {

};
